"use client";
import AuthInvitation from "@components/auth/auth-invitation/AuthInvitation";
import BagOverview from "@components/commerce/cart/BagOverview";
import { checkoutPageGridStyles } from "@features/checkout/components/CheckoutStyles";
import ContactDetailsStepForm from "@features/checkout/components/contact-details-step/ContactDetailsStepForm";
import DeliveryStepForm from "@features/checkout/components/delivery-step/DeliveryStepForm";
import type {
    CheckoutFormInitialValues,
    CheckoutFormValues,
} from "@features/checkout/components/delivery-step/types";
import { HeapPaymentStepForm } from "@features/checkout/components/payment-step/HeapPaymentStepForm/HeapPaymentStepForm";
import { HeapPaymentStepWithTotalZeroForm } from "@features/checkout/components/payment-step/HeapPaymentStepWithTotalZeroForm";
import { usePlacedOrderCart } from "@features/checkout/components/payment-step/hooks/usePlacedOrderCart";
import SupportModuleBox from "@features/checkout/components/SupportModuleBox";
import { CheckoutStep, ShippingMethodTypes } from "@features/checkout/types";
import { formatStreet } from "@components/store-locator/utils/formatAddress";
import { activeSiteConfig } from "@config/site/site-config";
import type { IPageCheckout } from "@contentful-api/types/contentful";
import { useOrderItemsLimit } from "@hooks/useOrderItemsLimit";
import { useCartItemsAvailability } from "@hooks/useCartItemsAvailability";
import { useIsLoyaltyMember } from "@hooks/useIsLoyaltyMember";
import { useShippingMethods } from "@features/checkout/hooks/useShippingMethods";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { CheckoutProgressSubtypes } from "@lib/analytics/analyticsTypes";
import { useAuth } from "@lib/auth/useAuth";
import { Checkout, CommonCMS } from "@lib/constants/contentful";
import { isDateAndTimeEnabledForDeliveryMethod } from "@lib/constants/dateSelectorDeliveryMethods";
import { getPagePathByLocale, shoppingBagPage } from "@lib/constants/pagePaths";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { ClientFetchService } from "@lib/helpers/clientFetchService";
import { LogTag, Logger, ServiceType } from "@lib/monitoring/logger";
import { validateStoreData } from "@lib/utils/checkout-utility/validateStoreData";
import { clearInvalidStoreData } from "@lib/utils/checkout-utility/clearInvalidStoreData";
import {
    buildBillingAddressAction,
    buildDeliveryDateAndTimeSlotActions,
    buildShippingAddressAction,
    buildShippingMethodAction,
} from "@lib/utils/checkout-utility/deliveryStepActions";
import { countryCodeLookup } from "@lib/utils/countryUtility";
import { executeCommerceToolsWithRetry } from "@lib/utils/executeCommerceToolsWithRetry";
import { useStores } from "@features/stores/client";
import {
    getCartDiscount,
    getCartSummaryDiscount,
    getCartSummarySubTotalPrice,
    getCartDisplayTotalPrice,
    getUsedCartDiscountCodeIds,
} from "@lib/utils/cart-utility/cartUtility";
import { cartContainsNonExpressProducts } from "@lib/utils/cart-utility/nonExpressDeliveryProducts";
import {
    shapeAddressCustomFields,
    shapeContactDetailsCustomFields,
} from "@lib/utils/cartProductUtility";
import { isAmazonRedirectAction } from "@lib/utils/checkout-utility/amazon-pay/amazonPay";
import { useCheckoutErrorState } from "@features/checkout/utils/useCheckoutErrorState";
import {
    anonymizeCartAddress,
    anonymizeCartContactDetails,
    useCheckout,
} from "@lib/utils/checkout-utility/checkoutUtility";
import { mapProfileAddressToCheckout } from "@features/checkout/utils/mapProfileAddressToCheckout";
import type { ContactDetailsInitialValues, DeliveryInitialValues } from "@lib/utils/checkoutHelper";
import {
    billingAndDeliveryAreEqual,
    sanitizeRegionalAddressFields,
} from "@lib/utils/checkoutHelper";
import { mapApplePayAddressToCheckout } from "@lib/utils/express-pay/apple-pay/mapApplePayAddressLines";
import { findHomeDelivery, getDeliveryMethodKey } from "@lib/utils/findDeliveryMethods";
import { getLineItemsQuantityWithoutGiftWrapping } from "@lib/utils/getLineItemsQuantityWithoutGiftWrapping";
import { cleanPhoneNumber, formatPhoneNumberByCountry } from "@lib/utils/phoneNumber";
import { useGetMyActiveCart } from "@lib/commerce/hooks/cartOperations";
import { useUpdateCart } from "@lib/commerce/hooks/checkoutOperations";
import { useLock } from "@store/hooks";
import { Heading } from "@ecco/ui/heading";
import { Text } from "@ecco/ui/text";
import { Accordion } from "@ecco/ui/accordion";
import { Spinner } from "@ecco/ui/spinner";
import { Flex } from "@ecco/ui/flex";
import { Progress } from "@ecco/ui/progress";
import { Grid } from "@ecco/ui/grid";
import { VStack } from "@ecco/ui/stack";
import { VisuallyHidden } from "@ecco/ui/visually-hidden";
import { space } from "@ecco/ui/tokens";
import { useRouter } from "@i18n/routing";
import { isEmpty } from "lodash-es";
import { useLocale } from "next-intl";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import Script from "next/script";
import { useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "@store/index";
import { RichText } from "@components/cms/rich-text-renderer/RichText";
import type { SiteSettings } from "@lib/contentful/types";
import type { Cart, MyCartUpdateAction } from "@graphql/generated/types";
import { useCart } from "@hooks/useCart";
import { getDatadogRum } from "@lib/monitoring/datadogHelper";
import { hasGraphQLErrors } from "@lib/utils/graphql/hasGraphQLErrors";
import { cartHasClickAndCollect } from "@lib/utils/cart-utility/cartHasClickAndCollect";
import { cartIsEmpty } from "@lib/utils/cart-utility/cartIsEmpty";
import type { ValidationModalResponse } from "@features/checkout/components/validation/address-validation/delivery-address-validation/components/AddressValidationModal";
import { AddressValidationModal } from "@features/checkout/components/validation/address-validation/delivery-address-validation/components/AddressValidationModal";
import { getAddressValidator } from "@features/checkout/components/validation/address-validation/delivery-address-validation/validateDeliveryAddress";
import { checkHouseNumber } from "@features/checkout/components/validation/address-validation/delivery-address-validation/checkHouseNumber";
import { isFailureAddressValidatorResult } from "@features/checkout/components/validation/address-validation/delivery-address-validation/asserters";
import { useModalLauncher } from "@hooks/useModalLauncher";
import type { AddressValidatorResult } from "@features/checkout/components/validation/address-validation/delivery-address-validation/types";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";
import { getPostalCodeValidationResult } from "@features/checkout/components/delivery-step/utils/postal-code-validation/validatePostalCode";
import { useFormFieldsMicrocopy } from "@features/checkout/components/delivery-step/FormFields.microcopy";
import {
    CHECKOUT_ERRORS,
    type CheckoutErrorType,
    HEAP_PAYMENT_ERRORS,
    type HeapPaymentErrorType,
} from "@lib/utils/checkout-utility/types";

const EmptyShoppingBag = dynamic(() => import("@features/checkout/components/EmptyShoppingBag"), {
    ssr: false,
});
// Dynamic import for ExpressCheckout
const ExpressCheckout = dynamic(
    () =>
        import("@components/commerce/cart/express-checkout/ExpressCheckout").then((mod) => ({
            default: mod.ExpressCheckout,
        })),
    {
        loading: () => <Progress variant="checkout" isIndeterminate />,
    }
);

interface CheckoutClientProps {
    page: IPageCheckout;
    reCaptchaAgreement?: SiteSettings["legal"]["reCaptchaAgreement"];
}

type InitializeStatus = "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";

export default function CheckoutClient({ page, reCaptchaAgreement }: CheckoutClientProps) {
    const { open: openValidationModal, portal: validationModalPortal } =
        useModalLauncher<ValidationModalResponse>({});

    const {
        fields: {
            support,
            newsletterConsentTitle,
            newsletterConsentPolicy,
            authorizationInvitationTitle,
            authorizationInvitationDescription,
            authorizationInvitation,
            policyAgreement,
            shippingDetails,
            contactErrotText,
        } = {},
    } = page;
    const authTitleAndDescription =
        authorizationInvitationTitle && authorizationInvitationDescription;

    const lock = useLock();
    const locale = useLocale();

    const { push } = useRouter();
    const { isLoggedIn, status } = useAuth();
    const {
        isLoyaltyMember,
        isKnown: isLoyaltyMembershipKnown,
        isLoading: isLoyaltyMemberLoading,
    } = useIsLoyaltyMember();
    const { getMultiple } = useMicrocopy();
    const searchParams = useSearchParams();
    const { setStepError, errors, clearAllErrors, clearStepError } = useCheckoutErrorState();

    const hasExpressCheckout = useFeatureFlag("hasExpressCheckout");
    const isStateFieldEnabled = useFeatureFlag("isStateFieldEnabled");
    const isDistrictFieldEnabled = useFeatureFlag("isDistrictFieldEnabled");
    const { headers: formFieldHeaders } = useFormFieldsMicrocopy();

    const microcopies = getMultiple(CommonCMS.page, [
        Checkout.emptyBagText,
        Checkout.shoppingBagTitle,
        Checkout.shoppingBagSummaryTitle,
        Checkout.step1.title,
        Checkout.step1.formEmail.error,
        Checkout.step1.formPhone.error,
        Checkout.step2.title,
        Checkout.step3.title,
        Checkout.headline,
        Checkout.step1.contactDetails.error,
        Checkout.step1.delivery.error,
        Checkout.step2.missingDeliveryMethodError,
        Checkout.step2.storeUnavailableError,
        Checkout.step2.formErrorFromPayment.addressInvalid,
        Checkout.step2.formErrorFromPayment.addressObjectMissing,
        Checkout.step2.formErrorFromPayment.nameAndLastNameOrCompanyNameMissing,
        Checkout.step2.formErrorFromPayment.postCodeMissing,
        Checkout.step2.formErrorFromPayment.streetNameMissing,
        Checkout.step2.formErrorFromPayment.validation,
        Checkout.step2.unavailableDeliveryMethodForHazardousProductsError,
        Checkout.step2.formErrorFromPayment.CCStoreAddressInvalid,
        Checkout.step2.formErrorFromPayment.deliveryMethodInvalid,
    ]);

    const getStoreData = useStore((state) => state.getStoreData);
    const storeData = useStore((state) => state.data.storeData);
    const setStoreData = useStore((state) => state.setStoreData);
    const getParcelShopData = useStore((state) => state.getParcelShopData);
    const parcelShopData = useStore((state) => state.data.parcelShopData);
    const previousPickupStoresRef = useRef({ storeData, parcelShopData });
    const getCart = useStore((state) => state.getCart);
    const customer = useStore((state) => state.data.me.customer);
    const { recalculateCart } = useCart();

    const defaultAddress = useMemo(
        () => customer?.addresses?.find((address) => address.isDefault),
        [customer?.addresses]
    );

    const [initializeStatus, setInitializeStatus] = useState<InitializeStatus>("NOT_STARTED");
    const { addContactDetails, initializeFormValues, cart: storeCart } = useCheckout();
    // Keeps the cart while a placed UPI order is being paid.
    const { cart, isResolvingPlacedOrder } = usePlacedOrderCart(
        storeCart,
        initializeStatus === "COMPLETED"
    );
    const { hasOutOfStockItems } = useCartItemsAvailability({
        cartId: cart?.id,
        lineItems: cart?.lineItems,
        enabled: hasExpressCheckout && status === "authenticated",
    });

    const executeGetMyActiveCart = useGetMyActiveCart();
    const executeUpdateCart = useUpdateCart();

    const { data: shippingMethods } = useShippingMethods();

    const filteredShippingMethods = useMemo(() => {
        if (
            !activeSiteConfig.hasNonExpressDeliveryProducts ||
            !cartContainsNonExpressProducts(cart)
        ) {
            return shippingMethods;
        }
        return shippingMethods?.filter((method) => method.fulfillmentOption !== "HD_EXPR");
    }, [shippingMethods, cart]);

    const {
        country,
        checkout: { countryCode: phoneCountryCode },
        amazonPay,
    } = activeSiteConfig;

    const { data: availableStores = [], isLoading: isLoadingStores } = useStores({
        country: country,
        locale: locale,
        queryConfig: {
            enabled: status === "authenticated",
        },
    });

    // State for handle and re-trigger Adyen component whenever the promo codes change. This will create an updated session.
    const [cartOrSessionChanged, setCartOrSessionChanged] = useState<Readonly<boolean>>(false);
    const [expandedIndex, setExpandedIndex] = useState<Readonly<CheckoutStep>>(
        CheckoutStep.CONTACT_DETAILS
    );
    const [formValues, setFormValues] = useState<Readonly<CheckoutFormValues>>();
    const [contactDetailsValidationField, setContactDetailsValidationField] = useState<
        "email" | "phone"
    >();
    // Live "order as company" value from the delivery form, so the auth invitation
    // hides as soon as the checkbox is ticked — before the step is submitted.
    const [isCompanyOrder, setIsCompanyOrder] = useState<Readonly<boolean>>(false);

    const visibleOnMobile = ["flex", "flex", "none", "none"];
    const visibleOnDesktop = ["none", "none", "flex", "flex"];
    const CCKey = getDeliveryMethodKey(ShippingMethodTypes.CLICK_AND_COLLECT);

    // Don't show express payment on payment step. If the user already made it there we want them to use a regular payment method.
    const showExpressPayment =
        hasExpressCheckout && !hasOutOfStockItems && expandedIndex !== CheckoutStep.PAYMENT;

    const scrollToError = () => {
        setTimeout(() => {
            const errorEl = document.querySelector("[data-checkout-error]");
            errorEl?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
    };

    const logCheckoutInfo = (message: string, c: Cart, additionalPayload?: any) => {
        Logger.info(ServiceType.WEB, `${message} cartId: ${c?.id}`, {
            tag: LogTag.CHECKOUT,
            step: expandedIndex,
            cart: summarizeCart(c),
            ...additionalPayload,
        });
    };

    const logCheckoutError = (message: string, c: Cart, additionalPayload: any = {}) => {
        Logger.error(ServiceType.WEB, `${message} cartId: ${c?.id}`, {
            tag: LogTag.CHECKOUT,
            step: expandedIndex,
            cart: summarizeCart(c),
            ...additionalPayload,
        });
    };

    const summarizeCart = (c: Cart | null) => {
        if (!c) return null;

        return {
            cartId: c.id,
            version: c.version,
            checkoutStep: expandedIndex,
            lineItems: c.lineItems?.length ?? 0,
            totalCentAmount: c.totalPrice?.centAmount,
            taxedTotalGross: c.taxedPrice?.totalGross?.centAmount,
            shippingMethods: shippingMethods?.map((m) => m.key) ?? [],
            filteredShippingMethods: filteredShippingMethods?.map((m) => m.key) ?? [],
            shippingMethodChosen: c.shippingInfo?.shippingMethod?.key,
            discount: getCartDiscount(c),
            shippingAddress: anonymizeCartAddress(c.shippingAddress),
            billingAddress: anonymizeCartAddress(c.billingAddress),
            contactDetails: anonymizeCartContactDetails(c.shippingAddress),
        };
    };

    useOrderItemsLimit(country, { shouldRedirect: true });

    useEffect(() => {
        getDatadogRum()?.startSessionReplayRecording();
    }, []);

    useEffect(() => {
        // If we are in contact details step, we don't need update cart data
        if (expandedIndex === CheckoutStep.CONTACT_DETAILS || !isLoggedIn) {
            logCheckoutInfo("Checkout cart sync skipped", cart, {
                reason:
                    expandedIndex === CheckoutStep.CONTACT_DETAILS
                        ? "contact_details_step"
                        : "not_authenticated",
            });
            return;
        }

        const paymentSessionId = searchParams.get("sessionId");
        const redirectResult = searchParams.get("redirectResult");

        const syncStepsAfterAuth = async () => {
            try {
                const { errors } = await addContactDetails(formValues.contactDetailsValues);
                if (errors?.length) {
                    throw new Error("Error syncing contact details after auth");
                }
                if (expandedIndex === CheckoutStep.PAYMENT) {
                    await deliveryStepSubmitHandler({ ...formValues });
                    setCartOrSessionChanged((prev) => !prev);
                }
            } catch (error) {
                Logger.error(ServiceType.WEB, "Error on addContactDetails", {
                    tag: LogTag.CHECKOUT,
                    error,
                });
                push(getPagePathByLocale(shoppingBagPage, locale));
            }
        };

        // If user creates an auth cart and not complete the order and then create an anonymous cart, we need to merge the carts
        // at the moment we will give priority to the latest cart, so the anonymous.
        // If this exist, that means that the user is coming from a paymentProvider and we need don't need to retrigger the add to cart
        if (!paymentSessionId && !redirectResult && initializeStatus === "COMPLETED") {
            syncStepsAfterAuth();
        }
    }, [cart?.anonymousId]);

    useEffect(() => {
        logCheckoutInfo("Checkout step changed", cart);
    }, [expandedIndex]);

    // Clear a delivery error when the shopper selects a new Click & Collect store or parcel shop.
    useEffect(() => {
        const hasNewSelection =
            (storeData && storeData !== previousPickupStoresRef.current.storeData) ||
            (parcelShopData && parcelShopData !== previousPickupStoresRef.current.parcelShopData);
        previousPickupStoresRef.current = { storeData, parcelShopData };

        if (
            hasNewSelection &&
            expandedIndex === CheckoutStep.DELIVERY &&
            formValues?.deliveryMethodId &&
            (formValues.deliveryMethodId === CCKey ||
                formValues.deliveryMethodId === ShippingMethodTypes.PARCEL_SHOP) &&
            (!isEmpty(storeData) || !isEmpty(parcelShopData))
        ) {
            clearStepError(CheckoutStep.DELIVERY);
        }
    }, [
        storeData,
        parcelShopData,
        expandedIndex,
        formValues?.deliveryMethodId,
        CCKey,
        clearStepError,
    ]);

    // Track cart changes to trigger payment session reload
    useEffect(() => {
        if (!cart) {
            return;
        }

        // Trigger payment session reload when cart price changes (e.g., after discount application, delivery address change)
        setCartOrSessionChanged((prev) => !prev);
    }, [cart?.taxedPrice?.totalGross?.centAmount]);

    // Guard against hazardous products being added (from another tab) while on payment step with express delivery selected.
    // If the cart now contains non-express products but the chosen shipping method is express (HD_EXPR), force the user
    // back to the delivery step to pick a compatible method before payment can proceed.
    useEffect(() => {
        if (
            !activeSiteConfig.hasNonExpressDeliveryProducts ||
            expandedIndex !== CheckoutStep.PAYMENT ||
            !cart
        ) {
            return;
        }

        const chosenMethodKey = cart.shippingInfo?.shippingMethod?.key;
        const isExpressDelivery = shippingMethods?.some(
            (method) => method.key === chosenMethodKey && method.fulfillmentOption === "HD_EXPR"
        );

        if (cartContainsNonExpressProducts(cart) && isExpressDelivery) {
            setStepError(
                CheckoutStep.DELIVERY,
                microcopies[Checkout.step2.unavailableDeliveryMethodForHazardousProductsError]
            );
            setExpandedIndex(CheckoutStep.DELIVERY);
        }
    }, [cart?.version, expandedIndex]);

    useEffect(() => {
        const isAmazonRedirect = isAmazonRedirectAction();

        if (searchParams.get("redirectResult") || isAmazonRedirect) {
            logCheckoutInfo("Checkout redirected from payment provider", cart, {
                isAmazonRedirect,
            });
            setExpandedIndex(CheckoutStep.PAYMENT);
            return;
        }

        const wasRedirectInProgress = sessionStorage.getItem("adyen_payment_method_type");
        if (wasRedirectInProgress) {
            logCheckoutInfo("Checkout returned from payment redirect without completing", cart);
            setExpandedIndex(CheckoutStep.PAYMENT);
        }
    }, [searchParams]);

    useEffect(() => {
        const initialize = async () => {
            if (status !== "authenticated" || initializeStatus !== "NOT_STARTED") return;
            setInitializeStatus("IN_PROGRESS");
            logCheckoutInfo("Checkout form initialization start", cart);

            // Fill out the initial form values with latest cart data
            await executeGetMyActiveCart();
            await recalculateCart();

            const currentCart = getCart();
            if (!currentCart) {
                Logger.warn(ServiceType.WEB, "No active cart found on init", {
                    tag: LogTag.CHECKOUT,
                });
                setInitializeStatus("COMPLETED");
                return;
            }

            const initValues = initializeFormValues();
            setFormValues({
                ...initValues,
            });

            logCheckoutInfo("Checkout form initialized", currentCart);
            AnalyticsClient.beginCheckout(currentCart.lineItems);
            AnalyticsClient.checkoutProgress(CheckoutProgressSubtypes.beginCheckout);
            setInitializeStatus("COMPLETED");
        };

        initialize();
    }, [status, initializeStatus, cart?.id]);

    if (initializeStatus === "COMPLETED" && !formValues && !cartIsEmpty(cart)) {
        setFormValues(initializeFormValues(cart));
    }

    if (initializeStatus !== "COMPLETED" || isResolvingPlacedOrder) {
        return <Spinner my={"25%"} mx={"50%"} />;
    }

    if (cartIsEmpty(cart)) {
        return <EmptyShoppingBag label={microcopies[Checkout.emptyBagText]} />;
    }

    const currentCartTotalPrice = getCartDisplayTotalPrice(cart);

    const contactDetailsSubmitHandler = lock(
        async (values: Readonly<ContactDetailsInitialValues>) => {
            setContactDetailsValidationField(undefined);
            const cart = getCart();
            const { shippingAddress } = cart;

            logCheckoutInfo("Contact details submit start", cart, {
                subscribed: !!values.isSubscribed,
                policyAccepted: !!values.policy,
            });

            if (values.isSubscribed) {
                // Intentionally not await the subscription call. It's very unlikely to fail and we dont want to keep increase the time between steps.
                ClientFetchService.post<any, any>(`/api/email-subscription`, {
                    email: values.email,
                    locale: locale,
                });
            }

            const cleanedNumber = cleanPhoneNumber(values.phone, country);
            const phoneNumber = `${phoneCountryCode}${cleanedNumber}`;
            const { errors: contactDetailsErrors, data } = await addContactDetails({
                ...values,
                phone: phoneNumber,
                customFields: shapeContactDetailsCustomFields(shippingAddress),
            });

            logCheckoutInfo("Contact details updated", cart);
            logCheckoutInfo("Contact details updated - updated cart", data?.updateMyCart);

            const isClickAndCollect = cartHasClickAndCollect(cart);
            const cartShippingMethodId = cart.shippingInfo?.shippingMethod?.key;

            // If the cart has a delivery method, set it in the form. Otherwise get CC or HD.
            const deliveryMethodId = isClickAndCollect
                ? // If any line item has a supplyChannel, the cart is Click & Collect
                  CCKey
                : // Otherwise check if the cart has a previous shipping method, otherwise set the default shipping method
                  cartShippingMethodId || findHomeDelivery(filteredShippingMethods)?.key;

            const storeData = getStoreData();
            setFormValues({
                ...formValues,
                contactDetailsValues: {
                    ...values,
                    phone: formatPhoneNumberByCountry(values.phone, country),
                    initialized: true,
                },
                hasSameBillingAddress: !isEmpty(storeData)
                    ? !isClickAndCollect
                    : formValues?.hasSameBillingAddress,
                deliveryMethodId,
            });

            const hasError = hasGraphQLErrors(contactDetailsErrors);
            if (hasError) {
                logCheckoutError("Errors in contact details submission", cart, {
                    errors: contactDetailsErrors,
                });
                setStepError(
                    CheckoutStep.CONTACT_DETAILS,
                    microcopies[Checkout.step1.contactDetails.error]
                );
            }

            if (!hasError) {
                clearAllErrors();
                setExpandedIndex(CheckoutStep.DELIVERY);
                logCheckoutInfo("Proceeding to delivery step", cart, {
                    deliveryMethodId,
                });
            }

            AnalyticsClient.addShippingInfo(cart?.lineItems, "");
        }
    );

    const getAddressValidatorAction = async (
        address: CheckoutFormInitialValues["deliveryAddress"],
        shippingMethodId: string
    ) => {
        const addressValidator = getAddressValidator(country);
        if (!addressValidator) {
            return;
        }

        return await addressValidator({
            address,
            shippingMethodId,
            checkoutType: "regular",
        });
    };

    const getValidationModalResult = async (
        validationResult: AddressValidatorResult,
        userAddress: CheckoutFormInitialValues["deliveryAddress"]
    ) => {
        if (validationResult.isValid !== false || validationResult.action !== "modal") {
            return;
        }

        return await openValidationModal(({ isShown, onClose }) => (
            <AddressValidationModal
                isOpen={isShown}
                onClose={onClose}
                validationResult={validationResult}
                userAddress={userAddress}
            />
        ));
    };

    const deliveryStepSubmitHandler = lock(async (values: CheckoutFormInitialValues) => {
        const isPickupMethod =
            values.deliveryMethodId === CCKey ||
            values.deliveryMethodId === getDeliveryMethodKey(ShippingMethodTypes.PARCEL_SHOP);
        const isCompanyOrder =
            values.hasSameBillingAddress && !isPickupMethod
                ? Boolean(values.deliveryAddress.isCompany)
                : Boolean(values.billingAddress.isCompany);

        if (isLoyaltyMemberLoading || (isCompanyOrder && !isLoyaltyMembershipKnown)) return;

        let { deliveryAddress, billingAddress, hasSameBillingAddress } = { ...values };
        if (isLoyaltyMember) {
            deliveryAddress = { ...deliveryAddress, isCompany: false, company: "" };
            billingAddress = { ...billingAddress, isCompany: false, company: "" };
        }
        const vatNumber = isLoyaltyMember ? "" : values.vatNumber;
        const { deliveryMethodId: formDeliveryMethodId } = { ...values };
        const selectedDeliveryMethod = filteredShippingMethods?.find(
            (method) => method.key === formDeliveryMethodId
        );
        const isSelectedDeliveryValid = !!selectedDeliveryMethod;

        const deliveryMethodId = selectedDeliveryMethod?.key;

        if (!isSelectedDeliveryValid || !deliveryMethodId) {
            setStepError(
                CheckoutStep.DELIVERY,
                microcopies[Checkout.step2.missingDeliveryMethodError]
            );
            scrollToError();
            return;
        }

        // Single cart read: the whole delivery step is committed in one updateMyCart at the
        // end, so no cart mutation runs before it and this snapshot's version stays valid.
        const cart = getCart();
        const {
            shippingAddress,
            shippingInfo,
            billingAddress: cartBillingAddress,
            id: cartId,
            version: cartVersion,
            lineItems,
        } = cart;

        const isPickUpMethod =
            deliveryMethodId === CCKey || deliveryMethodId === ShippingMethodTypes.PARCEL_SHOP;

        // Mandatory postal code validation before proceeding to payment.
        // This prevents bypassing the debounced field-level validation via fast paste or autofill.
        if (!isPickUpMethod && deliveryAddress.postalCode) {
            try {
                const postalCodeResult = await getPostalCodeValidationResult({
                    postalCode: deliveryAddress.postalCode,
                    country,
                    isBillingForm: false,
                    carrierCode: selectedDeliveryMethod?.carrierCode ?? undefined,
                    headers: formFieldHeaders,
                });
                if (postalCodeResult && postalCodeResult.isValid === false) {
                    Logger.error(ServiceType.WEB, "Postal code validation failed on submit", {
                        tag: LogTag.CHECKOUT,
                        cartId,
                        country,
                        carrierCode: selectedDeliveryMethod?.carrierCode,
                        validationKey: postalCodeResult.key,
                    });
                    setStepError(
                        CheckoutStep.DELIVERY,
                        postalCodeResult.message || microcopies[Checkout.step1.delivery.error]
                    );
                    scrollToError();
                    return;
                }
            } catch (error) {
                Logger.error(ServiceType.WEB, "Error validating postal code on submit", {
                    tag: LogTag.CHECKOUT,
                    error,
                });
                setStepError(CheckoutStep.DELIVERY, microcopies[Checkout.step1.delivery.error]);
                scrollToError();
                return;
            }
        }

        try {
            // Runs before the market validator so only one modal can appear per submit.
            const houseNumberResult = checkHouseNumber({
                address: deliveryAddress,
                shippingMethodId: formDeliveryMethodId,
                country,
            });

            if (isFailureAddressValidatorResult(houseNumberResult)) {
                const houseNumberModalResult = await getValidationModalResult(
                    houseNumberResult,
                    deliveryAddress
                );

                // User cancelled the flow to return to address editing.
                if (houseNumberModalResult?.action === "cancel") {
                    return;
                }
            }

            // Skipped once the shopper confirmed a street without a house number:
            // the market validator cannot resolve that and would open a second modal.
            const addressValidationAction = houseNumberResult.isValid
                ? await getAddressValidatorAction(deliveryAddress, formDeliveryMethodId)
                : undefined;

            // If modal action is required, render the modal and wait for it's result.
            if (
                addressValidationAction?.isValid === false &&
                addressValidationAction?.action === "modal"
            ) {
                const modalResult = await getValidationModalResult(
                    addressValidationAction,
                    deliveryAddress
                );

                // User cancelled the flow to return to address editing.
                if (modalResult?.action === "cancel") {
                    return;
                }
                // User accepted a suggested address. Update the delivery address accordingly and continue.
                if (modalResult?.action === "update") {
                    deliveryAddress = {
                        ...deliveryAddress,
                        ...modalResult.address,
                    };
                }
            }

            // Address validation returned an error action. We won't continue on errors.
            if (
                addressValidationAction?.isValid === false &&
                addressValidationAction?.action === "error"
            ) {
                setStepError(CheckoutStep.DELIVERY, microcopies[Checkout.step1.delivery.error]);
                scrollToError();
                return;
            }
        } catch (error) {
            Logger.error(ServiceType.WEB, "Error validating delivery address", {
                tag: LogTag.CHECKOUT,
                error,
            });
            setStepError(CheckoutStep.DELIVERY, microcopies[Checkout.step1.delivery.error]);
            scrollToError();
            return;
        }

        // Date/time not applicable for this method → null out. The values are folded into the
        // single cart update below (no separate mutation).
        if (!isDateAndTimeEnabledForDeliveryMethod(deliveryMethodId, locale)) {
            deliveryAddress.date = null;
            deliveryAddress.timeslot = null;
        }

        let storeData = getStoreData();

        if (deliveryMethodId === ShippingMethodTypes.PARCEL_SHOP) {
            storeData = getParcelShopData();
            // Validate that parcel shop is selected
            if (isEmpty(storeData)) {
                setStepError(
                    CheckoutStep.DELIVERY,
                    microcopies[Checkout.step2.missingDeliveryMethodError]
                );
                scrollToError();
                return;
            }
        } else if (deliveryMethodId === CCKey) {
            // Validate that store is selected for Click and Collect
            if (isEmpty(storeData)) {
                setStepError(
                    CheckoutStep.DELIVERY,
                    microcopies[Checkout.step2.missingDeliveryMethodError]
                );
                scrollToError();
                return;
            }

            if (isLoadingStores) {
                return;
            }

            if (!lineItems.at(-1)) {
                Logger.error(ServiceType.WEB, "No line items found in cart", {
                    tag: LogTag.CHECKOUT,
                    cartId,
                });
                return;
            }

            const validatedStore = validateStoreData(storeData, availableStores ?? []);
            if (!validatedStore) {
                try {
                    await clearInvalidStoreData({
                        storeData,
                        cartId,
                        cartVersion,
                        availableStoreCount: availableStores.length,
                        lineItems,
                        locale,
                        setStoreData,
                        executeUpdateCart,
                    });
                } catch (error) {
                    Logger.error(ServiceType.WEB, "Failed to clear invalid store data", {
                        tag: LogTag.CHECKOUT,
                        error,
                        cartId,
                    });
                }

                setStepError(
                    CheckoutStep.DELIVERY,
                    microcopies[Checkout.step2.storeUnavailableError]
                );

                setExpandedIndex(CheckoutStep.DELIVERY);

                scrollToError();
                return;
            }

            // Update store with the validated data. The supply channel is written to every
            // line item as part of the single cart update below.
            storeData = validatedStore;
            setStoreData(validatedStore);
        }

        const getDeliveryNameFields = (): Required<
            Pick<
                CheckoutFormInitialValues["deliveryAddress"],
                "firstName" | "lastName" | "company" | "isCompany"
            >
        > => {
            // If it's a pick-up-method, we use the values from billing address instead.
            const targetAddressObject = isPickUpMethod ? billingAddress : deliveryAddress;

            // If it's a company order we don't want firstName+lastName.
            if (targetAddressObject.isCompany) {
                return {
                    lastName: "",
                    firstName: "",
                    company: targetAddressObject.company,
                    isCompany: targetAddressObject.isCompany,
                };
            }
            return {
                firstName: targetAddressObject.firstName,
                lastName: targetAddressObject.lastName,
                company: "",
                isCompany: targetAddressObject.isCompany,
            };
        };

        const getDHLParcelShopFields = (): Required<
            Pick<CheckoutFormInitialValues["deliveryAddress"], "salutation" | "customerNumber">
        > => {
            if (deliveryMethodId === ShippingMethodTypes.PARCEL_SHOP) {
                return {
                    salutation: billingAddress.salutation ?? "",
                    customerNumber: billingAddress.customerNumber ?? "",
                };
            }
            return {
                salutation: "",
                customerNumber: "",
            };
        };

        const deliveryAddressPayload: CheckoutFormInitialValues["deliveryAddress"] =
            sanitizeRegionalAddressFields(
                {
                    ...deliveryAddress,
                    streetName: isPickUpMethod
                        ? formatStreet(storeData)
                        : deliveryAddress.streetName,
                    country: deliveryAddress.country,
                    additionalAddressInfo: isPickUpMethod
                        ? ""
                        : deliveryAddress.additionalAddressInfo,
                    postalCode: isPickUpMethod ? storeData?.postalCode : deliveryAddress.postalCode,
                    city: isPickUpMethod ? storeData?.city : deliveryAddress.city,
                    region: isPickUpMethod ? storeData?.region : deliveryAddress.region, // @TODO: Split check for region in two parts. Otherwise, if region is empty and is not CC, it uses the store region.
                    ...getDHLParcelShopFields(),
                    ...getDeliveryNameFields(),
                },
                { isStateFieldEnabled, isDistrictFieldEnabled }
            );

        const customDeliveryFields = shapeAddressCustomFields(
            deliveryAddressPayload,
            deliveryMethodId === ShippingMethodTypes.PARCEL_SHOP ? getParcelShopData() : undefined
        );

        // Temporary logging for troubleshooting purposes
        Logger.info(ServiceType.WEB, "deliveryStep - addDeliveryAddress", {
            tag: LogTag.CHECKOUT,
            formValues: deliveryAddress,
            customFields: customDeliveryFields,
            payload: deliveryAddressPayload,
            cartId,
        });

        // Is a condition that CC orders never will have possibility to have "hasSameBillingAddress=true"....
        // So billing address will be the same as delivery address only if the checkbox is checked and is not a CC order.
        if (isPickUpMethod) {
            deliveryAddress = {
                ...deliveryAddressPayload,
                country: values.deliveryAddress.country,
            };
            hasSameBillingAddress = false;
        }

        // If user marked "hasSameBillingAddress" we will base billing address on delivery address.
        if (hasSameBillingAddress) {
            billingAddress = {
                ...deliveryAddress,
            };
        }

        const getBillingNameFields = (): Required<
            Pick<
                CheckoutFormInitialValues["billingAddress"],
                "firstName" | "lastName" | "company" | "isCompany"
            >
        > => {
            if (billingAddress.isCompany) {
                return {
                    firstName: "",
                    lastName: "",
                    isCompany: billingAddress.isCompany,
                    company: billingAddress.company,
                };
            }
            return {
                firstName: billingAddress.firstName,
                lastName: billingAddress.lastName,
                isCompany: billingAddress.isCompany,
                company: "",
            };
        };

        const billingAddressPayload = sanitizeRegionalAddressFields(
            {
                ...billingAddress,
                ...getBillingNameFields(),
                vatNumber,
            },
            { isStateFieldEnabled, isDistrictFieldEnabled }
        );

        const customBillingFields = shapeAddressCustomFields(billingAddressPayload);

        // Temporary logging for troubleshooting purposes
        Logger.info(ServiceType.WEB, "deliveryStep - addBillingAddress", {
            tag: LogTag.CHECKOUT,
            formValues: billingAddress,
            customFields: customBillingFields,
            payload: billingAddressPayload,
            cartId,
        });

        // Resolve the CommerceTools country codes (async) concurrently before composing the actions.
        const [deliveryCountry, billingCountry] = await Promise.all([
            deliveryAddressPayload.country
                ? countryCodeLookup(deliveryAddressPayload.country)
                : shippingAddress?.country,
            billingAddressPayload.country
                ? countryCodeLookup(billingAddressPayload.country)
                : undefined,
        ]);

        // A missing resolved country is a validation failure, not a cart-mutation error — block
        // the step here rather than fabricating transport-layer errors for the aggregation below.
        if (!deliveryCountry || !billingCountry) {
            logCheckoutError("Missing resolved country for delivery step", cart, {
                deliveryCountry,
                billingCountry,
                deliveryMethodId,
            });
            setStepError(CheckoutStep.DELIVERY, microcopies[Checkout.step1.delivery.error]);
            scrollToError();
            return;
        }

        const supplyChannelActions: MyCartUpdateAction[] =
            deliveryMethodId === CCKey
                ? lineItems.map((lineItem) => ({
                      setLineItemSupplyChannel: {
                          lineItemId: lineItem.id,
                          supplyChannel: { id: storeData?.supplyChannel?.[0]?.id },
                      },
                  }))
                : [];

        // Commit the whole delivery step in one updateMyCart. setShippingMethod is last
        // because CommerceTools re-validates it against the shipping address set above.
        const actions: MyCartUpdateAction[] = [
            ...buildDeliveryDateAndTimeSlotActions(cart, {
                date: deliveryAddress.date || null,
                timeslot: deliveryAddress.timeslot || null,
            }),
            ...supplyChannelActions,
            buildShippingAddressAction(
                shippingAddress,
                { ...deliveryAddressPayload, country: deliveryCountry },
                customDeliveryFields
            ),
            buildBillingAddressAction(
                cartBillingAddress,
                {
                    ...billingAddressPayload,
                    country: billingCountry,
                    email: shippingAddress?.email,
                },
                [...customBillingFields]
            ),
            buildShippingMethodAction(deliveryMethodId),
        ];

        const { errors: deliveryStepErrors } = await executeCommerceToolsWithRetry(
            executeUpdateCart,
            {
                cartId,
                cartVersion,
                storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
                locale,
                actions,
            },
            0,
            "deliveryStep updateCart"
        );

        const formValuesFromCart = initializeFormValues();

        // Update the formvalues regardless of errors so we are in sync
        setFormValues({
            ...formValuesFromCart,
            deliveryAddress: {
                ...deliveryAddressPayload,
                initialized: true,
            },
            billingAddress: {
                ...billingAddressPayload,
            },
            deliveryMethodId: deliveryMethodId ?? findHomeDelivery(filteredShippingMethods).key,
        });

        const hasError = hasGraphQLErrors(deliveryStepErrors);

        if (hasError) {
            logCheckoutError("Errors in delivery step handler", cart, {
                deliveryStepErrors,
                deliveryMethodId,
            });
            setStepError(CheckoutStep.DELIVERY, microcopies[Checkout.step1.delivery.error]);
            scrollToError();
        }

        if (!hasError) {
            AnalyticsClient.addShippingInfo(lineItems, shippingInfo?.shippingMethodName);
            clearAllErrors();
            AnalyticsClient.checkoutProgress(CheckoutProgressSubtypes.goToPaymentSelections);
            setExpandedIndex(CheckoutStep.PAYMENT);
            logCheckoutInfo("Proceeding to payment step", cart, {
                deliveryMethodId,
            });
        }
    });

    const defaultCheckoutAddress = mapProfileAddressToCheckout(defaultAddress, country);

    const paymentErrorHandler = (
        errorType: HeapPaymentErrorType | CheckoutErrorType,
        errValue?: string
    ) => {
        logCheckoutError("Payment error in HEAP flow", cart, {
            errorType,
        });

        switch (errorType) {
            case HEAP_PAYMENT_ERRORS.Validation:
            case HEAP_PAYMENT_ERRORS.AddressInvalid:
                setStepError(
                    CheckoutStep.DELIVERY,
                    microcopies[Checkout.step2.formErrorFromPayment.validation]
                );
                break;
            case HEAP_PAYMENT_ERRORS.AddressObjectMissing:
                setStepError(
                    CheckoutStep.DELIVERY,
                    microcopies[Checkout.step2.formErrorFromPayment.addressObjectMissing]
                );
                setFormValues((prev) => ({
                    ...prev,
                    deliveryAddress: {},
                    billingAddress: {},
                }));
                break;
            case HEAP_PAYMENT_ERRORS.PostCodeMissing:
                setStepError(
                    CheckoutStep.DELIVERY,
                    microcopies[Checkout.step2.formErrorFromPayment.postCodeMissing]
                );
                setFormValues((prev) => ({
                    ...prev,
                    deliveryAddress: {
                        ...prev.deliveryAddress,
                        postalCode: "",
                    },
                    billingAddress: {
                        ...prev.billingAddress,
                        postalCode: "",
                    },
                }));
                break;
            case HEAP_PAYMENT_ERRORS.StreetNameMissing:
                setStepError(
                    CheckoutStep.DELIVERY,
                    microcopies[Checkout.step2.formErrorFromPayment.streetNameMissing]
                );
                setFormValues((prev) => ({
                    ...prev,
                    deliveryAddress: {
                        ...prev.deliveryAddress,
                        streetName: "",
                    },
                    billingAddress: {
                        ...prev.billingAddress,
                        streetName: "",
                    },
                }));
                break;
            case HEAP_PAYMENT_ERRORS.NameAndLastNameOrCompanyNameMissing:
                setFormValues((prev) => ({
                    ...prev,
                    deliveryAddress: {
                        ...prev.deliveryAddress,
                        firstName: "",
                        lastName: "",
                        company: "",
                    },
                    billingAddress: {
                        ...prev.billingAddress,
                        firstName: "",
                        lastName: "",
                        company: "",
                    },
                }));
                setStepError(
                    CheckoutStep.DELIVERY,
                    microcopies[
                        Checkout.step2.formErrorFromPayment.nameAndLastNameOrCompanyNameMissing
                    ]
                );
                break;

            case CHECKOUT_ERRORS.MissingEmail:
            case CHECKOUT_ERRORS.EmailInvalid:
                setContactDetailsValidationField("email");
                setFormValues((prev) => ({
                    ...prev,
                    contactDetailsValues: {
                        ...prev.contactDetailsValues,
                        email: errValue || "",
                    },
                }));
                setStepError(
                    CheckoutStep.CONTACT_DETAILS,
                    microcopies[Checkout.step1.formEmail.error]
                );
                break;
            case CHECKOUT_ERRORS.MissingPhone:
            case CHECKOUT_ERRORS.PhoneInvalid:
                setContactDetailsValidationField("phone");
                setFormValues((prev) => ({
                    ...prev,
                    contactDetailsValues: {
                        ...prev.contactDetailsValues,
                        phone: errValue || "",
                    },
                }));
                setStepError(
                    CheckoutStep.CONTACT_DETAILS,
                    microcopies[Checkout.step1.formPhone.error]
                );
                break;
            case CHECKOUT_ERRORS.CCStoreAddressInvalid:
                setStoreData(null);
                setStepError(
                    CheckoutStep.DELIVERY,
                    microcopies[Checkout.step2.formErrorFromPayment.CCStoreAddressInvalid]
                );
                break;
            case CHECKOUT_ERRORS.DeliveryMethodInvalid:
                setStepError(
                    CheckoutStep.DELIVERY,
                    microcopies[Checkout.step2.formErrorFromPayment.deliveryMethodInvalid]
                );
                break;
            case CHECKOUT_ERRORS.ExpressUnavailableForProducts:
                setStepError(
                    CheckoutStep.DELIVERY,
                    microcopies[Checkout.step2.unavailableDeliveryMethodForHazardousProductsError]
                );
                break;
            default:
                break;
        }
    };

    const renderPaymentStep = () => {
        const sharedProps = {
            stepId: CheckoutStep.PAYMENT,
            errorMessage: errors?.[CheckoutStep.PAYMENT],
            handleExpanded: setExpandedIndex,
            isExpandedId: expandedIndex,
            title: microcopies[Checkout.step3.title],
            recaptchaAgreement: <RichText data={reCaptchaAgreement} />,
            isSubscribed: !!formValues?.contactDetailsValues?.isSubscribed,
        };
        const isZeroValueOrder = Number(currentCartTotalPrice?.centAmount) === 0;

        if (isZeroValueOrder) {
            return (
                <HeapPaymentStepWithTotalZeroForm
                    {...sharedProps}
                    currentCartTotalPrice={currentCartTotalPrice}
                    cartId={cart.id}
                />
            );
        }

        return (
            <HeapPaymentStepForm
                {...sharedProps}
                orderCreationError={<RichText data={contactErrotText} />}
                cartOrSessionChanged={cartOrSessionChanged}
                cartId={cart.id}
                cart={cart}
                onStepError={paymentErrorHandler}
            />
        );
    };

    const handleExpressValidationError = (deliveryAddress?: DeliveryInitialValues) => {
        // sync formValues with the updated cart from express.
        const initValues = initializeFormValues();
        const deliveryAddressValues = {
            ...initValues.deliveryAddress,
            ...deliveryAddress,
            ...(deliveryAddress && { initialized: true }),
        };
        const mappedDeliveryAddress = mapApplePayAddressToCheckout(
            deliveryAddressValues,
            activeSiteConfig.country
        );
        const mappedBillingAddress = mapApplePayAddressToCheckout(
            initValues.billingAddress,
            activeSiteConfig.country,
            "billing"
        );
        setFormValues({
            ...initValues,
            deliveryAddress: mappedDeliveryAddress,
            billingAddress: mappedBillingAddress,
            hasSameBillingAddress: billingAndDeliveryAreEqual({
                billing: mappedBillingAddress,
                delivery: mappedDeliveryAddress,
            }),
        });
        // Reset the step to make sure contact detail are validated.
        setExpandedIndex(CheckoutStep.CONTACT_DETAILS);
    };

    return (
        <>
            {validationModalPortal}
            {amazonPay?.enabled && amazonPay?.checkoutJsUrl && (
                <Script
                    // Script should be beforeInteractive so we know it's ready when components mount
                    strategy="lazyOnload"
                    src={amazonPay?.checkoutJsUrl}
                />
            )}
            <VisuallyHidden>
                <Heading as={"h1"}>{microcopies[Checkout.headline]}</Heading>
            </VisuallyHidden>
            <Grid data-testid="checkout-page-content" {...checkoutPageGridStyles}>
                {!isLoggedIn && !isCompanyOrder && (
                    <Flex
                        px={[3, 4]}
                        py={[6, 6]}
                        order="-3"
                        display={visibleOnMobile}
                        direction="column"
                        bg="white"
                    >
                        {authTitleAndDescription && (
                            <Flex gap={1} display={visibleOnMobile} w={"100%"} flexDir={"column"}>
                                <Text fontWeight={"bold"}>{authorizationInvitationTitle}</Text>
                                <Text fontSize={"desktopBodyB4"}>
                                    {authorizationInvitationDescription}
                                </Text>
                            </Flex>
                        )}
                        <AuthInvitation
                            mt={authTitleAndDescription ? 2 : 0}
                            text={authorizationInvitation}
                        />
                    </Flex>
                )}

                <Accordion
                    defaultIndex={[expandedIndex]}
                    index={expandedIndex}
                    reduceMotion
                    minWidth="0"
                    gap={{ base: space["2"], lg: space["4"] }}
                    display="flex"
                    flexDirection={["column"]}
                >
                    {showExpressPayment && (
                        <Flex
                            px={6}
                            py={[4, 6]}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            bg={"white"}
                        >
                            <ExpressCheckout
                                hasDivider={false}
                                isCheckout={true}
                                onError={handleExpressValidationError}
                            />
                        </Flex>
                    )}
                    {!isLoggedIn && !isCompanyOrder && (
                        <Flex
                            px={6}
                            py={[4, 6]}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            bg={"white"}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            display={visibleOnDesktop}
                        >
                            {authTitleAndDescription && (
                                <Flex w={"100%"} flexDir={"column"}>
                                    <Text fontWeight={"bold"}>{authorizationInvitationTitle}</Text>
                                    <Text fontSize={"desktopBodyB4"}>
                                        {authorizationInvitationDescription}
                                    </Text>
                                </Flex>
                            )}
                            <AuthInvitation
                                text={authorizationInvitation}
                                textAlign={authTitleAndDescription ? "end" : "start"}
                            />
                        </Flex>
                    )}
                    {/* Each of these steps - e.g. components ending with "StepForm" are
                    actually a formik form. They are wrapped in an AccordionItem to allow
                    if they are expanded they show the actual form, otherwise the show a bunch of
                    text indicating what the previous edited state was.

                    This text view is shown by the StepFormValues component.
                    So if the form view is not working correctly go to these components down to the code,
                    but if it is the text view that is not working correctly go to StepFormValues component and try to see what
                    is happening there as a quick step to debug.
                    */}

                    <ContactDetailsStepForm
                        stepId={CheckoutStep.CONTACT_DETAILS}
                        errorMessage={errors?.[CheckoutStep.CONTACT_DETAILS]}
                        handleExpanded={setExpandedIndex}
                        isExpandedId={expandedIndex}
                        newsletterConsentTitle={newsletterConsentTitle}
                        newsletterConsentPolicy={newsletterConsentPolicy}
                        onSubmit={contactDetailsSubmitHandler}
                        initialValues={formValues.contactDetailsValues}
                        validationErrorField={contactDetailsValidationField}
                        title={microcopies[Checkout.step1.title]}
                        policyAgreement={policyAgreement}
                    />

                    <DeliveryStepForm
                        stepId={CheckoutStep.DELIVERY}
                        errorMessage={errors?.[CheckoutStep.DELIVERY]}
                        handleExpanded={setExpandedIndex}
                        isExpandedId={expandedIndex}
                        onSubmit={deliveryStepSubmitHandler}
                        initialValues={{
                            vatNumber: formValues.vatNumber,
                            hasSameBillingAddress: formValues.hasSameBillingAddress,
                            initialized: formValues.deliveryAddress.initialized,
                            deliveryAddress:
                                // If there is defaultAddress and the form is not init, we will use defaultAddress from my addresses, otherwise form data
                                defaultCheckoutAddress && !formValues.deliveryAddress.initialized
                                    ? {
                                          ...defaultCheckoutAddress,
                                          country: activeSiteConfig.country,
                                      }
                                    : {
                                          ...formValues.deliveryAddress,
                                          country: activeSiteConfig.country,
                                      },
                            deliveryMethodId: formValues.deliveryMethodId,
                            billingAddress: formValues.billingAddress,
                        }}
                        title={microcopies[Checkout.step2.title]}
                        shippingMethods={filteredShippingMethods ?? []}
                        cart={cart}
                        shippingDetails={shippingDetails}
                        onCompanyOrderChange={setIsCompanyOrder}
                    ></DeliveryStepForm>

                    {renderPaymentStep()}
                </Accordion>
                <VStack
                    align="stretch"
                    spacing={space["0.2"]}
                    order={[-1, -1, -1, "initial"]}
                    minWidth="0"
                >
                    <BagOverview
                        locale={locale}
                        productsList={cart.lineItems}
                        cartTaxedPrice={cart.taxedPrice}
                        discount={getCartSummaryDiscount(cart)}
                        cartTotalPrice={currentCartTotalPrice}
                        subtotalPrice={getCartSummarySubTotalPrice(cart)}
                        shippingInfo={cart.shippingInfo}
                        totalQuantity={getLineItemsQuantityWithoutGiftWrapping(cart)}
                        bagHeader={microcopies[Checkout.shoppingBagTitle]}
                        withPromoCodeSection={true}
                        setCartOrSessionChanged={setCartOrSessionChanged}
                        cartOrSessionChanged={cartOrSessionChanged}
                        usedDiscountCodeIds={getUsedCartDiscountCodeIds(cart)}
                    />

                    <SupportModuleBox display={visibleOnDesktop} supportModule={support} p={6} />
                </VStack>
                <SupportModuleBox display={visibleOnMobile} supportModule={support} p={4} mt={2} />
            </Grid>
        </>
    );
}
